import * as React from "react";
import * as ProgressPrimitive from "@radix-ui/react-progress";

import { cn } from "@/lib/utils";

/**
 * <Progress value={0..100} tone? /> — тонкая полоска прогресса в нашей палитре.
 *
 * Track: `var(--rule-faint)`; fill: `var(--fg-1)` (default), `var(--alert-red)` при
 * `tone="danger"` (at-risk проекты) или `var(--clear-green)` при `tone="success"`
 * (кодирует «подтверждено», напр. недельная лента 116 §2.2). Радиус —
 * `var(--radius-chip)`. Дефолтная высота 6px, ширину задаём через className
 * (обычно `w-20` ~80px).
 */
type ProgressTone = "default" | "danger" | "success";

interface ProgressProps extends React.ComponentProps<typeof ProgressPrimitive.Root> {
  tone?: ProgressTone;
}

export function Progress({
  className,
  value,
  tone = "default",
  ...props
}: ProgressProps): React.ReactElement {
  const v = Math.max(0, Math.min(100, value ?? 0));
  const fill =
    tone === "danger"
      ? "var(--alert-red)"
      : tone === "success"
        ? "var(--clear-green)"
        : "var(--fg-1)";
  return (
    <ProgressPrimitive.Root
      data-slot="progress"
      data-tone={tone}
      value={v}
      className={cn("relative h-1.5 w-20 overflow-hidden", className)}
      style={{
        background: "var(--rule-faint)",
        borderRadius: "var(--radius-chip)",
      }}
      {...props}
    >
      <ProgressPrimitive.Indicator
        data-slot="progress-indicator"
        className="h-full transition-[width]"
        style={{
          width: `${v}%`,
          background: fill,
          borderRadius: "var(--radius-chip)",
        }}
      />
    </ProgressPrimitive.Root>
  );
}
